import { useEffect, useRef } from 'react';

import type { PageTrackingProps } from '../../components/Tracking/trackingTypes';

import { useTracking } from './useTracking';

declare global {
  interface Window {
    currentPageTrackingData: PageTrackingProps;
    hasTrackedPageView?: boolean;
  }
}

interface UsePageTrackingProps
  extends Omit<PageTrackingProps, 'seekAdvertiserId'> {
  shouldTrack?: boolean;
  accountContext?: {
    currentUser?: { id: string };
    currentAdvertiser?: { billingId: string };
  };
  authenticated?: boolean;
  loading?: boolean;
  page: string;
}

export const usePageTracking = ({
  shouldTrack = true,
  accountContext,
  authenticated,
  loading = false,
  page,
  ...pageTrackingProps
}: UsePageTrackingProps) => {
  const { trackView } = useTracking();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || hasTrackedRef.current) return;

    // For authenticated users, wait for loading to complete and accountContext to be available
    if (loading) return;
    if (authenticated && !accountContext) return;

    const combinedProps = {
      ...pageTrackingProps,
      page,
    } as PageTrackingProps;

    window.currentPageTrackingData = combinedProps;

    if (shouldTrack && !window.hasTrackedPageView) {
      trackView(page);
      window.hasTrackedPageView = true;
      hasTrackedRef.current = true;
    }
  }, [
    shouldTrack,
    trackView,
    pageTrackingProps,
    page,
    loading,
    authenticated,
    accountContext,
  ]);
};
